import React from "react";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { ArrowUpRight, ArrowDownLeft, Plus, Inbox, Receipt, Smartphone, Landmark, Globe, Banknote } from "lucide-react";

const iconFor = (tx) => {
  if (tx.type === "topup") return Plus;
  if (tx.type === "bill") return Receipt;
  if (tx.type === "recharge") return Smartphone;
  if (tx.type === "bank_transfer") return Landmark;
  if (tx.type === "intl_transfer") return Globe;
  if (tx.type === "financing" || tx.type === "financing_repay") return Banknote;
  return tx.direction === "in" ? ArrowDownLeft : ArrowUpRight;
};

const titleFor = (tx, t) => {
  if (tx.type === "topup") return t.type_topup;
  if (tx.type === "bill") return `${t.type_bill} · ${tx.counterparty_name}`;
  if (tx.type === "recharge") return `${t.type_recharge} · ${tx.counterparty_name}`;
  if (tx.type === "bank_transfer") return `${t.type_bank_transfer} · ${tx.counterparty_name}`;
  if (tx.type === "intl_transfer") return `${t.type_intl_transfer} · ${tx.counterparty_name}`;
  if (tx.type === "financing") return `${t.type_financing} · ${tx.counterparty_name}`;
  if (tx.type === "financing_repay") return `${t.type_financing_repay} · ${tx.counterparty_name}`;
  if (tx.direction === "in") return `${t.received} · ${tx.counterparty_name}`;
  return `${t.sent} · ${tx.counterparty_name}`;
};

export function TransactionRow({ tx, onClick }) {
  const { t, lang } = useApp();
  const Icon = iconFor(tx);
  const isIn = tx.direction === "in";
  const date = new Date(tx.created_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });

  return (
    <motion.button
      data-testid="transaction-item-row"
      onClick={onClick}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full flex items-center justify-between p-3.5 rounded-2xl bg-card hover:bg-muted/50 border border-border/50 transition-all duration-200 text-start"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className={`w-11 h-11 rounded-full grid place-items-center shrink-0 ${isIn ? "bg-primary/15 text-primary" : "bg-muted text-foreground/70"}`}>
          <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{titleFor(tx, t)}</p>
          <p className="text-xs text-muted-foreground">{date}</p>
        </div>
      </div>
      <div className="text-end shrink-0 ps-2">
        <p className={`font-bold tabular-nums text-sm ${isIn ? "text-primary" : "text-foreground"}`}>
          {isIn ? "+" : "−"}{formatSAR(tx.amount, lang)}
        </p>
        <p className="text-[11px] text-muted-foreground">{t.currency}</p>
      </div>
    </motion.button>
  );
}

export default function TransactionList({ transactions, onSelect }) {
  const { t } = useApp();

  if (!transactions?.length) {
    return (
      <div data-testid="transaction-history-list" className="flex flex-col items-center justify-center py-14 text-center">
        <div className="w-16 h-16 rounded-full bg-muted grid place-items-center mb-4">
          <Inbox className="w-7 h-7 text-muted-foreground" />
        </div>
        <p className="font-semibold">{t.noActivity}</p>
        <p className="text-sm text-muted-foreground mt-1">{t.noActivitySub}</p>
      </div>
    );
  }

  return (
    <div data-testid="transaction-history-list" className="space-y-2.5">
      {transactions.map((tx) => (
        <TransactionRow key={tx.tx_id} tx={tx} onClick={() => onSelect(tx)} />
      ))}
    </div>
  );
}
